import {
    AlertTriangle,
    ArrowDown,
    ArrowRight,
    ArrowUp,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    CircleAlert,
    Clock3,
    LoaderCircle,
    User,
    Wallet,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StaffLeaveCardScreen.module.css'

const TYPE_COLORS = {
    CL: '#1565C0',
    SL: '#E65100',
    PL: '#2E7D32',
    CO: '#6A1B9A',
}

const TX_TYPE_LABELS = {
    OPENING_BALANCE: 'Opening Balance',
    ACCRUAL: 'Monthly Accrual',
    UTILIZATION: 'Leave Taken',
    MANUAL_ADJUSTMENT: 'Manual Adjustment',
    CARRY_FORWARD: 'Carry Forward',
    LAPSE: 'Year-end Lapse',
    ENCASHMENT: 'Leave Encashment',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const fmtDate = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : value
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

const fmtDateTime = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : value
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const txTime = (tx) => {
    const dateStr = tx?.createdOn || tx?.transactionDate || ''
    const parsed = new Date(String(dateStr).includes(' ') ? String(dateStr).replace(' ', 'T') : dateStr)
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
}

const sortTransactionsAscending = (transactions = []) => {
    return [...transactions].sort((a, b) => txTime(a) - txTime(b))
}

const StaffLeaveCardScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const staffIdentifier = params?.staffIdentifier || location?.state?.staffIdentifier || ''
    const staffNameFromNav = location?.state?.staffName || ''

    const [leaveData, setLeaveData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [activeTab, setActiveTab] = useState('OVERVIEW')

    const fetchLeaveCard = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !staffIdentifier) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getStaffLeaveCard', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier,
                    year: selectedYear,
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load leave card.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                setLeaveData(respo?.response || null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (respo?.errorCode === 'VAL_007') {
                toast.error(respo?.message || 'Staff member not found.')
                navigate(-1)
                return
            }

            if (respo?.errorCode === 'VAL_002') {
                toast.error(respo?.message || 'Invalid year selected.')
                return
            }

            toast.error(respo?.message || 'Failed to load leave card.')
        } catch (error) {
            console.error('getStaffLeaveCard error:', error)
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate, selectedYear, staffIdentifier])

    useEffect(() => {
        fetchLeaveCard()
    }, [fetchLeaveCard])

    const balances = Array.isArray(leaveData?.balances) ? leaveData.balances : []
    const leaveTypes = useMemo(() => balances.map((item) => item.leaveType).filter(Boolean), [balances])

    useEffect(() => {
        if (activeTab === 'OVERVIEW') return
        if (!leaveTypes.includes(activeTab)) {
            setActiveTab('OVERVIEW')
        }
    }, [activeTab, leaveTypes])

    const allTransactions = useMemo(() => {
        const merged = balances.flatMap((balance) => {
            const tx = Array.isArray(balance?.transactions) ? balance.transactions : []
            return tx.map((entry) => ({ ...entry, leaveType: entry?.leaveType || balance?.leaveType }))
        })
        return sortTransactionsAscending(merged)
    }, [balances])

    const selectedBalance = useMemo(
        () => balances.find((item) => item?.leaveType === activeTab) || null,
        [activeTab, balances]
    )

    const selectedTransactions = useMemo(() => {
        if (!selectedBalance) return []
        return sortTransactionsAscending(selectedBalance?.transactions || [])
    }, [selectedBalance])

    const latestUpdatedOn = useMemo(() => {
        const timestamps = balances
            .map((item) => {
                const val = item?.updatedOn
                const parsed = new Date(String(val || '').replace(' ', 'T')).getTime()
                return Number.isFinite(parsed) ? parsed : 0
            })
            .filter((value) => value > 0)
        if (timestamps.length === 0) return ''
        return new Date(Math.max(...timestamps)).toISOString()
    }, [balances])

    const staffName = leaveData?.staffName || staffNameFromNav || 'Staff'
    const designationLabel = leaveData?.designationLabel || '—'
    const joiningDate = leaveData?.joiningDate

    const currentYear = new Date().getFullYear()
    const canGoPrevYear = selectedYear > 2020
    const canGoNextYear = selectedYear < currentYear + 1

    const openInitialize = () => {
        navigate('/payroll/setup/leave-balance-init', {
            state: {
                preselectedStaff: [{ staffIdentifier, staffName }],
            },
        })
    }

    const openManualAdjustment = (leaveType = 'CL') => {
        navigate(`/payroll/manual-leave-adjustment/${staffIdentifier}`, {
            state: {
                staffName,
                preselectedLeaveType: leaveType,
                currentBalances: balances,
            },
        })
    }

    if (!staffIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)} type="button">
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <h1 className={styles.headerTitle}>Staff Leave Card</h1>
                        <p className={styles.headerSub}>Staff identifier is missing.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)} type="button">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>{staffName} — Leave Card</h1>
                    <p className={styles.headerSub}>{designationLabel}</p>
                </div>
            </div>

            <div className={styles.yearSelector}>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setSelectedYear((prev) => prev - 1)}
                    disabled={!canGoPrevYear || isLoading}
                >
                    <ChevronLeft size={16} />
                </button>
                <span className={styles.yearLabel}>{selectedYear}</span>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setSelectedYear((prev) => prev + 1)}
                    disabled={!canGoNextYear || isLoading}
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {isLoading ? (
                <div className={styles.stateCard}>
                    <LoaderCircle size={18} className={styles.spinIcon} />
                    <span>Loading leave card...</span>
                </div>
            ) : !leaveData ? (
                <div className={`${styles.stateCard} ${styles.stateError}`}>
                    <CircleAlert size={16} />
                    <span>Unable to load leave data.</span>
                </div>
            ) : leaveData?.isInitialized === false ? (
                <div className={styles.emptyStateCard}>
                    <AlertTriangle size={18} />
                    <div>
                        <p className={styles.emptyTitle}>Leave balances not set up for {selectedYear}.</p>
                        <p className={styles.emptySub}>Initialize now to generate opening balances for this staff.</p>
                    </div>
                    <button type="button" className={styles.ctaBtn} onClick={openInitialize}>
                        Initialize Now
                        <ArrowRight size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.metaRow}>
                        <span className={styles.metaChip}>
                            <User size={13} /> Joined: {fmtDate(joiningDate)}
                        </span>
                        <span className={styles.metaChip}>
                            <Wallet size={13} /> Available: {toNumber(leaveData?.summary?.totalAvailableBalance).toFixed(2)}d
                        </span>
                        <span className={styles.metaChip}>
                            <CalendarDays size={13} /> Used: {toNumber(leaveData?.summary?.totalUsedThisYear).toFixed(2)}d
                        </span>
                        <span className={styles.metaChip}>
                            <Clock3 size={13} /> Tx: {toNumber(leaveData?.summary?.totalTransactions, 0)}
                        </span>
                    </div>

                    <div className={styles.actionRow}>
                        <button
                            type="button"
                            className={styles.secondaryCtaBtn}
                            onClick={() => openManualAdjustment(activeTab !== 'OVERVIEW' ? activeTab : 'CL')}
                        >
                            Add Manual Adjustment
                            <ArrowRight size={14} />
                        </button>
                    </div>

                    <div className={styles.tabRow}>
                        <button
                            type="button"
                            className={`${styles.tabBtn} ${activeTab === 'OVERVIEW' ? styles.tabBtnActive : ''}`}
                            onClick={() => setActiveTab('OVERVIEW')}
                        >
                            OVERVIEW
                        </button>
                        {leaveTypes.map((type) => (
                            <button
                                key={type}
                                type="button"
                                className={`${styles.tabBtn} ${activeTab === type ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab(type)}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'OVERVIEW' ? (
                        <>
                            <div className={styles.balanceScroller}>
                                {balances.map((balance) => (
                                    <LeaveBalanceCard
                                        key={balance?.leaveType}
                                        balance={balance}
                                        onPress={(type) => setActiveTab(type)}
                                    />
                                ))}
                            </div>

                            <div className={styles.sectionCard}>
                                <div className={styles.sectionHead}>All Transactions</div>
                                {allTransactions.length === 0 ? (
                                    <p className={styles.emptyTxText}>No transactions available for this year.</p>
                                ) : (
                                    <div className={styles.txList}>
                                        {allTransactions.map((tx, index) => (
                                            <TransactionRow key={`${tx?.transactionIdentifier || index}`} tx={tx} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {selectedBalance && (
                                <div className={styles.sectionCard}>
                                    <div className={styles.sectionHead}>
                                        {selectedBalance?.leaveTypeLabel || selectedBalance?.leaveType}
                                    </div>
                                    <div className={styles.detailGrid}>
                                        <StatRow label="Opening" value={selectedBalance?.openingBalance} />
                                        {toNumber(selectedBalance?.accruedBalance, 0) > 0 && (
                                            <StatRow label="Accrued" value={selectedBalance?.accruedBalance} />
                                        )}
                                        <StatRow label="Used" value={selectedBalance?.usedBalance} />
                                        {toNumber(selectedBalance?.encashedBalance, 0) > 0 && (
                                            <StatRow label="Encashed" value={selectedBalance?.encashedBalance} />
                                        )}
                                        {toNumber(selectedBalance?.lapsedBalance, 0) > 0 && (
                                            <StatRow label="Lapsed" value={selectedBalance?.lapsedBalance} />
                                        )}
                                    </div>
                                    <div className={styles.availableLine}>
                                        Available: {toNumber(selectedBalance?.availableBalance, 0).toFixed(2)} days
                                    </div>
                                </div>
                            )}

                            <div className={styles.sectionCard}>
                                <div className={styles.sectionHead}>Transactions ({activeTab})</div>
                                {selectedTransactions.length === 0 ? (
                                    <p className={styles.emptyTxText}>No transactions for this leave type.</p>
                                ) : (
                                    <div className={styles.txList}>
                                        {selectedTransactions.map((tx, index) => (
                                            <TransactionRow key={`${tx?.transactionIdentifier || index}`} tx={tx} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <p className={styles.footerNote}>Last updated: {fmtDateTime(latestUpdatedOn)}</p>
                </>
            )}
        </div>
    )
}

const LeaveBalanceCard = ({ balance, onPress }) => {
    const color = TYPE_COLORS[balance?.leaveType] || '#546E7A'

    return (
        <button
            type="button"
            className={styles.balanceCard}
            style={{ borderTopColor: color }}
            onClick={() => onPress(balance?.leaveType)}
        >
            <span className={styles.leaveTypeCode} style={{ color }}>
                {balance?.leaveType || '—'}
            </span>
            <span className={styles.leaveTypeLabel}>{balance?.leaveTypeLabel || 'Leave Type'}</span>

            <div className={styles.balanceFigures}>
                <div className={styles.figureBlock}>
                    <span className={styles.figureValue}>{toNumber(balance?.availableBalance, 0).toFixed(2)}</span>
                    <span className={styles.figureLabel}>Available</span>
                </div>
                <div className={styles.figureBlock}>
                    <span className={styles.figureValue}>{toNumber(balance?.usedBalance, 0).toFixed(2)}</span>
                    <span className={styles.figureLabel}>Used</span>
                </div>
                {toNumber(balance?.accruedBalance, 0) > 0 && (
                    <div className={styles.figureBlock}>
                        <span className={styles.figureValue}>{toNumber(balance?.accruedBalance, 0).toFixed(2)}</span>
                        <span className={styles.figureLabel}>Accrued</span>
                    </div>
                )}
            </div>
        </button>
    )
}

const TransactionRow = ({ tx }) => {
    const isCredit = Boolean(tx?.isCredit)
    const qty = toNumber(tx?.quantity, 0)

    return (
        <div className={`${styles.txRow} ${!isCredit ? styles.txRowDebit : ''}`}>
            <div className={styles.txLeft}>
                <span className={isCredit ? styles.txDirectionCredit : styles.txDirectionDebit}>
                    {isCredit ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                </span>
                <div className={styles.txMain}>
                    <p className={styles.txType}>{TX_TYPE_LABELS[tx?.transactionType] ?? tx?.transactionType}</p>
                    <p className={styles.txDate}>
                        {fmtDate(tx?.transactionDate)} • {tx?.leaveType || '—'}
                    </p>
                    {tx?.remarks ? <p className={styles.txRemarks}>{tx.remarks}</p> : null}
                </div>
            </div>

            <div className={styles.txRight}>
                <p className={isCredit ? styles.txQuantityCredit : styles.txQuantityDebit}>
                    {qty > 0 ? '+' : ''}
                    {qty.toFixed(2)}d
                </p>
                <p className={styles.txBalance}>
                    {toNumber(tx?.balanceBefore, 0).toFixed(2)} → {toNumber(tx?.balanceAfter, 0).toFixed(2)}
                </p>
            </div>
        </div>
    )
}

const StatRow = ({ label, value }) => (
    <div className={styles.statRow}>
        <span>{label}</span>
        <strong>{toNumber(value, 0).toFixed(2)}</strong>
    </div>
)

export default StaffLeaveCardScreen